<template>
  <div class="plots-grid" ref="containerRef">
    <div class="plots-grid__header">
      <div class="plots-grid__header-left">
        <span class="plots-grid__title">Naves del parque</span>
        <span class="plots-grid__count">({{ plots.length }})</span>
      </div>
      <div class="plots-grid__header-right">
        <button type="button" class="plots-grid__btn plots-grid__btn--ghost" @click="$emit('import-plots')">Importar</button>
        <button type="button" class="plots-grid__btn" @click="$emit('add-plot')">＋ Agregar nave</button>
        <input
          v-model="searchText"
          type="text"
          class="plots-grid__search"
          placeholder="Buscar nave o inquilino..."
        />
        <select v-model="statusFilter" class="plots-grid__filter">
          <option value="">Todos</option>
          <option value="OCCUPIED">Ocupadas</option>
          <option value="VACANT">Disponibles</option>
          <option value="RESERVED">En negociación</option>
          <option value="MAINTENANCE">En mantenimiento</option>
        </select>
      </div>
    </div>

    <div v-if="filteredPlots.length === 0" class="plots-grid__empty-filter">
      Ninguna nave coincide con la búsqueda o el filtro.
    </div>

    <div
      v-else
      class="plots-grid__body"
      :style="{ gridTemplateColumns: `repeat(${gridConfig.columns}, 1fr)` }"
    >
      <button
        v-for="plot in filteredPlots"
        :key="plot.id"
        class="plot-card"
        :class="[`plot-card--${gridConfig.size}`, `plot-card--${plot.status.toLowerCase()}`]"
        :style="{ height: gridConfig.cardHeight + 'px' }"
        @click="$emit('select-plot', plot)"
        @mouseenter="hoveredPlot = plot"
        @mousemove="onCardMouseMove"
        @mouseleave="hoveredPlot = null"
      >
        <span
          v-if="plot.openTicketCount > 0"
          class="plot-card__ticket-badge"
          :style="{ background: TICKET_PRIORITY_COLORS[plot.topTicketPriority] }"
          title="Tickets de mantenimiento abiertos"
        >{{ plot.openTicketCount }}</span>
        <span class="plot-card__code">{{ plot.name }}</span>
        <span class="plot-card__center">
          {{ plot.currentTenant?.name || (plot.status === 'VACANT' ? 'Disponible' : STATUS_LABELS[plot.status]) }}
        </span>
        <span v-if="gridConfig.size !== 'sm' && plot.areaM2" class="plot-card__area">{{ plot.areaM2 }} m²</span>
        <span v-if="gridConfig.size === 'lg' && plot.contracts?.[0]" class="plot-card__rent">
          {{ formatCurrency(plot.contracts[0].monthlyRent) }}
        </span>
      </button>
    </div>

    <div
      v-if="hoveredPlot"
      class="plots-grid__tooltip"
      :style="{ left: tooltipPos.x + 'px', top: tooltipPos.y + 'px' }"
    >
      <span class="plots-grid__tooltip-name">{{ hoveredPlot.name }}</span>
      <span class="plots-grid__tooltip-tenant">{{ hoveredPlot.currentTenant?.name || 'Vacante' }}</span>
      <span v-if="hoveredPlot.areaM2" class="plots-grid__tooltip-line">{{ hoveredPlot.areaM2 }} m²</span>
      <span v-if="hoveredPlot.contracts?.[0]" class="plots-grid__tooltip-line">
        {{ formatCurrency(hoveredPlot.contracts[0].monthlyRent) }}/mes
      </span>
      <span v-if="hoveredDaysRemaining !== null" class="plots-grid__tooltip-line">
        {{ hoveredDaysRemaining }} días restantes
      </span>
      <span v-if="hoveredPlot.openTicketCount > 0" class="plots-grid__tooltip-line plots-grid__tooltip-line--ticket">
        {{ hoveredPlot.openTicketCount }} ticket{{ hoveredPlot.openTicketCount > 1 ? 's' : '' }} de mantenimiento abierto{{ hoveredPlot.openTicketCount > 1 ? 's' : '' }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'

const props = defineProps({
  plots: { type: Array, default: () => [] },
})

defineEmits(['select-plot', 'add-plot', 'import-plots'])

const STATUS_LABELS = {
  OCCUPIED: 'Ocupada',
  VACANT: 'Disponible',
  MAINTENANCE: 'Mantenimiento',
  RESERVED: 'En negociación',
}

const TICKET_PRIORITY_COLORS = {
  CRITICAL: '#e84545',
  HIGH: '#f97316',
  MEDIUM: '#f0a500',
  LOW: '#94a3b8',
}

const searchText = ref('')
const statusFilter = ref('')

const filteredPlots = computed(() => {
  const q = searchText.value.trim().toLowerCase()
  return props.plots.filter((p) => {
    if (statusFilter.value === 'MAINTENANCE') {
      if (p.status !== 'MAINTENANCE' && !(p.openTicketCount > 0)) return false
    } else if (statusFilter.value && p.status !== statusFilter.value) return false
    if (!q) return true
    return p.name.toLowerCase().includes(q) || (p.currentTenant?.name || '').toLowerCase().includes(q)
  })
})

const gridConfig = computed(() => {
  const total = props.plots.length
  if (total < 12) return { columns: 3, cardHeight: 120, size: 'lg' }
  if (total <= 24) return { columns: 4, cardHeight: 96, size: 'md' }
  if (total <= 36) return { columns: 5, cardHeight: 72, size: 'sm' }
  return { columns: 6, cardHeight: 72, size: 'sm' }
})

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount)
}

const hoveredPlot = ref(null)
const tooltipPos = reactive({ x: 0, y: 0 })
const containerRef = ref(null)

function onCardMouseMove(e) {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  tooltipPos.x = e.clientX - rect.left + 14
  tooltipPos.y = e.clientY - rect.top + 14
}

const hoveredDaysRemaining = computed(() => {
  const contract = hoveredPlot.value?.contracts?.[0]
  if (!contract) return null
  const diffMs = new Date(contract.endDate).getTime() - Date.now()
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
})
</script>

<style scoped>
.plots-grid {
  position: relative;
  width: 100%;
  height: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.plots-grid__header {
  position: relative;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.9rem;
  border-bottom: 1px solid #f1f5f9;
  gap: 0.6rem;
}

.plots-grid__header-left {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-shrink: 0;
}

.plots-grid__title {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.plots-grid__count {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 10px;
  color: #9ca3af;
}

.plots-grid__header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  min-width: 0;
}

.plots-grid__btn {
  height: 26px;
  padding: 0 0.6rem;
  border: 1px solid #3b82f6;
  border-radius: 5px;
  background: #3b82f6;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.plots-grid__btn:hover { background: #2563eb; border-color: #2563eb; }

.plots-grid__btn--ghost {
  background: #ffffff;
  color: #3b82f6;
}

.plots-grid__btn--ghost:hover { background: #eff6ff; }

.plots-grid__search {
  width: 160px;
  height: 26px;
  padding: 0 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--text-content);
}

.plots-grid__search:focus {
  outline: none;
  border-color: #3b82f6;
}

.plots-grid__filter {
  height: 26px;
  padding: 0 0.4rem;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--text-content);
  background: #ffffff;
}

.plots-grid__empty-filter {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-family: var(--font-body);
  font-size: 0.8rem;
}

.plots-grid__body {
  flex: 1;
  min-height: 0;
  display: grid;
  gap: 0.6rem;
  padding: 0.8rem;
  overflow-y: auto;
  align-content: start;
  scrollbar-width: thin;
  scrollbar-color: var(--border-active) transparent;
}

.plots-grid__body::-webkit-scrollbar { width: 6px; }
.plots-grid__body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.plots-grid__body::-webkit-scrollbar-track { background: transparent; }

.plot-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  padding: 0.3rem;
  text-align: center;
  transition: background 0.15s ease;
}

.plot-card--occupied { background: rgba(16, 185, 129, 0.1); border-color: rgba(16, 185, 129, 0.4); }
.plot-card--occupied:hover { background: rgba(16, 185, 129, 0.2); }

.plot-card--reserved { background: rgba(240, 165, 0, 0.1); border-color: rgba(240, 165, 0, 0.4); }
.plot-card--reserved:hover { background: rgba(240, 165, 0, 0.2); }

.plot-card--vacant { background: rgba(59, 130, 246, 0.1); border-color: rgba(59, 130, 246, 0.4); }
.plot-card--vacant:hover { background: rgba(59, 130, 246, 0.2); }

.plot-card--maintenance { background: rgba(232, 69, 69, 0.1); border-color: rgba(232, 69, 69, 0.4); }
.plot-card--maintenance:hover { background: rgba(232, 69, 69, 0.2); }

.plot-card__ticket-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  border: 2px solid #ffffff;
  box-sizing: content-box;
}

.plot-card__code {
  font-family: var(--font-mono);
  font-weight: 700;
  color: var(--text-content);
}

.plot-card--lg .plot-card__code { font-size: 13px; }
.plot-card--md .plot-card__code { font-size: 12px; }
.plot-card--sm .plot-card__code { font-size: 11px; }

.plot-card__center {
  font-family: var(--font-body);
  color: var(--text-content);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plot-card--lg .plot-card__center { font-size: 12px; font-weight: 500; }
.plot-card--md .plot-card__center { font-size: 11px; font-weight: 500; }
.plot-card--sm .plot-card__center { font-size: 10px; font-weight: 400; color: #6b7280; }

.plot-card__area {
  font-family: var(--font-body);
  font-size: 10px;
  color: #9ca3af;
}

.plot-card--occupied .plot-card__rent { color: #10b981; }
.plot-card--reserved .plot-card__rent { color: #f0a500; }
.plot-card--vacant .plot-card__rent { color: #3b82f6; }
.plot-card--maintenance .plot-card__rent { color: #e84545; }

.plot-card__rent {
  font-family: var(--font-mono);
  font-size: 11px;
}

.plots-grid__tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(14, 23, 41, 0.92);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  z-index: 5;
  white-space: nowrap;
}

.plots-grid__tooltip-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #ffffff;
}

.plots-grid__tooltip-tenant {
  font-size: 0.75rem;
  color: #cbd5e1;
}

.plots-grid__tooltip-line {
  font-size: 0.72rem;
  font-family: var(--font-mono);
  color: #94a3b8;
}

.plots-grid__tooltip-line--ticket {
  color: #f97316;
  font-family: var(--font-body);
}
</style>